/**
 * Les Moves d'un exercice ou d'un défi, branchés sur la feuille v2 : ce qui est débloqué (sur l'appareil),
 * les cartes de la trousse (publiées dans `TrousseToolsProvider`), les touches à cacher, le panneau du
 * Move ouvert et le « nouveau geste » que la main montre. Une seule source pour le player d'exercice et
 * le défi live : le comportement est le même.
 *
 * La démo guidée garde son propre câblage (`demo/guided/guided-round.tsx`) : son coach pilote chaque
 * écran du panneau.
 */
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTrousseToolsPublisher } from '@/features/trousse/trousse-tools';
import { addUserMove, userMovesKey, useUserMoves } from '@/lib/moves/user-moves';
import type { TrousseItem } from '@/features/trousse/trousse-items';
import type { V2Session } from '@/features/brouillon-v2/v2-session';
import type { V2Ui } from '@/features/scratch-sheet/scratch-data';
import {
  gesturesInPlay,
  lockedGestureKeys,
  moveById,
  placementOverrides,
  type MovePlacement,
  sheetPredLines,
  type MoveDef,
} from './move-catalog';
import type { HoraReward } from './hora-reward-pop';
import { useMoveCatalog } from './move-catalog-store';
import { pickMove, type MovePanelState } from './move-panel-state';
import {
  lockableGestures,
  neededGesture,
  playerMoveEntries,
  readUnlockedMoves,
  writeUnlockedMoves,
  type PlayerMoveEntry,
} from './player-moves';
import type { MoveShelf } from './move-catalog';

const noSubscribe = () => () => {};
const zero = () => 0;

export interface PlayerMoves {
  /** les familles de touches à ne pas montrer (gestes non débloqués) — clavier ET ligne en cours */
  hiddenKeys: readonly string[];
  /** la place des touches de chaque geste réglée dans l'admin (`kind` de rangée → place) */
  placement: Readonly<Record<string, MovePlacement>>;
  /** le panneau du Move ouvert (il REMPLACE le clavier), ou null */
  panel: { shelf: MoveShelf; state: MovePanelState } | null;
  setPanelState: (state: MovePanelState) => void;
  closePanel: () => void;
  unlocked: ReadonlySet<string>;
  remember: (id: string) => void;
  /** le nouveau geste que la question demande et qui n'est pas pris : la main montre la trousse */
  needed: MoveDef | null;
  /** la trousse s'ouvre ou se referme (pour montrer la trousse fermée, puis la carte) */
  onKitToggle: (open: boolean) => void;
  kitOpen: boolean;
  /** un geste verrouillé a été tenté sur la ligne (glisser un terme) */
  onLockedGesture: (gesture: 'commute') => void;
  /** la pastille « +N HORA » d'un Move qu'on vient de débloquer (HORA globales), ou null */
  reward: HoraReward | null;
  clearReward: () => void;
}

export function usePlayerMoves({
  enabled,
  level,
  model,
  session,
  ui,
  userId,
  cardIds,
}: {
  /** faux : rien n'est publié, rien n'est caché (feuille sans moteur v2, aperçu…) */
  enabled: boolean;
  level: string | null | undefined;
  /** le modèle de la feuille (ses `preds` décident des connaissances proposées) */
  model: unknown;
  session: V2Session | null;
  ui: V2Ui | null;
  /** le compte (jamais une session anonyme) : ses Moves sont en base ; null = gardés sur l'appareil */
  userId: string | null | undefined;
  /** les SKILLCARDS de la question en cours (un Move associé passe devant dans la trousse) */
  cardIds?: readonly string[];
}): PlayerMoves {
  /*
   * Ce qui est débloqué : EN BASE pour un compte (`user_moves`, migration 0750 — l'admin le voit et
   * peut le retirer), sur l'appareil sans compte (visiteur, session anonyme). Les Moves pris pendant
   * la page s'ajoutent tout de suite (`added`), sans attendre la base.
   */
  const server = useUserMoves(userId, enabled);
  const [added, setAdded] = useState<ReadonlySet<string>>(() =>
    userId ? new Set<string>() : readUnlockedMoves(null),
  );
  useEffect(() => {
    setAdded(userId ? new Set<string>() : readUnlockedMoves(null));
  }, [userId]);
  const serverIds = server.data;
  const unlocked = useMemo<ReadonlySet<string>>(() => {
    if (!userId) return added;
    const out = new Set(added);
    for (const r of serverIds ?? []) out.add(r.move_id);
    return out;
  }, [userId, added, serverIds]);
  const qc = useQueryClient();
  // la grosse pastille d'HORA : seulement pour un Move VRAIMENT nouveau pour ce compte
  const [reward, setReward] = useState<HoraReward | null>(null);
  const known = useRef(unlocked);
  known.current = unlocked;
  const seq = useRef(0);
  const remember = useCallback(
    (id: string) => {
      const m = moveById(id);
      if (m && !known.current.has(id) && m.hora > 0) {
        seq.current += 1;
        setReward({ seq: seq.current, name: m.name, hora: m.hora, kind: m.kind });
      }
      setAdded((prev) => {
        if (prev.has(id)) return prev;
        const next = new Set(prev).add(id);
        if (!userId) writeUnlockedMoves(null, next);
        return next;
      });
      if (userId)
        void addUserMove(userId, id)
          .then(() => qc.invalidateQueries({ queryKey: userMovesKey(userId) }))
          .catch(() => {
            /* pas de réseau : acquis le temps de la page, repris au prochain déblocage */
          });
    },
    [userId, qc],
  );

  // la feuille change (sélection, ligne validée) : on relit ce que l'écriture permet
  const version = useSyncExternalStore(
    session?.subscribe ?? noSubscribe,
    session?.getSnapshot ?? zero,
    session?.getSnapshot ?? zero,
  );
  const [triedCommute, setTriedCommute] = useState(false);
  useEffect(() => setTriedCommute(false), [session]);

  const inPlay = useMemo(() => {
    void version;
    if (!enabled || !session || !ui) return [];
    let kinds: string[] = [];
    try {
      kinds = ui.keyboardFor(session).tools.map((r) => r.kind);
    } catch {
      kinds = [];
    }
    let movable = triedCommute;
    try {
      movable ||= session.moveOptions().length > 0;
    } catch {
      /* pas de sélection */
    }
    if (movable) kinds.push('commute');
    return gesturesInPlay(kinds);
  }, [enabled, session, ui, version, triedCommute]);

  // le catalogue des Moves vient de la base (admin) : il se pose dès qu'il arrive
  const catalog = useMoveCatalog();
  const cardKey = useMemo(() => [...(cardIds ?? [])].sort(), [cardIds]);
  const predLines = useMemo(() => (enabled ? sheetPredLines(model) : []), [enabled, model]);
  const entries = useMemo<PlayerMoveEntry[]>(
    () =>
      enabled ? playerMoveEntries({ level, predLines, unlocked, inPlay, cardIds: cardKey }) : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `cardKey` est la clé stable de `cardIds`
    [enabled, level, predLines, unlocked, inPlay, cardKey.join(','), catalog],
  );
  const needed = neededGesture(entries);

  const [panel, setPanel] = useState<{ shelf: MoveShelf; state: MovePanelState } | null>(null);
  // une autre question : le panneau parlait de la précédente
  useEffect(() => setPanel(null), [session]);

  useTrousseToolsPublisher(
    'player-moves',
    () =>
      entries.map((e): TrousseItem => {
        const savoir = e.move.kind === 'savoir';
        if (e.tab === 'cours')
          return {
            id: `move-${e.move.id}`,
            label: e.move.name,
            note: e.move.why,
            mark: '✦',
            group: 'moves',
            tab: 'cours',
            tone: 'savoir',
            readOnly: true,
            onPick: () => {},
          };
        return {
          id: `move-${e.move.id}`,
          label: e.move.name,
          mark: savoir ? '✦' : '↹',
          group: 'moves',
          tone: savoir ? 'savoir' : 'geste',
          pending: !e.owned,
          // un GESTE à débloquer se désigne (il n'y a rien à deviner) ; une connaissance jamais :
          // la choisir parmi ses voisines EST l'exercice
          highlight: e.needed,
          onPick: () => setPanel({ shelf: e.shelf, state: pickMove(e.shelf, e.move.id, unlocked) }),
        };
      }),
    [entries, unlocked],
  );

  const [kitOpen, setKitOpen] = useState(false);
  const hiddenKeys = useMemo(
    () => (enabled ? lockedGestureKeys(unlocked, lockableGestures(level)) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- le catalogue change ce qui se verrouille
    [enabled, unlocked, level, catalog],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps -- le catalogue porte les places
  const placement = useMemo(() => placementOverrides(), [catalog]);

  return {
    hiddenKeys,
    placement,
    panel,
    setPanelState: (state) => setPanel((p) => (p ? { ...p, state } : p)),
    closePanel: () => setPanel(null),
    unlocked,
    remember,
    needed,
    onKitToggle: setKitOpen,
    kitOpen,
    onLockedGesture: () => setTriedCommute(true),
    reward,
    clearReward: () => setReward(null),
  };
}
